import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Centro de acción del ticket: crear tareas, chat, WhatsApp, captura, IA + relacionados.
// Reutiliza endpoints /alarm-tickets/{id}/* y componentes existentes. Respeta permisos.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Paper, Box, Typography, Stack, Button, IconButton, Tooltip, Chip, Divider, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem as MuiMenuItem, Autocomplete,
  CircularProgress, Checkbox, FormControlLabel, List, ListItem, ListItemText, Alert, useMediaQuery,
} from '@mui/material';
import {
  AddTask as AddTaskIcon, AutoAwesome as AIIcon, Chat as ChatIcon, WhatsApp as WhatsAppIcon,
  PhotoCamera as SnapIcon, MoreVert as MoreIcon, Download as DownloadIcon, ContentCopy as CopyIcon,
  OpenInNew as OpenIcon, Lightbulb as SuggestIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toPng, toBlob } from 'html-to-image';
import { saveAs } from 'file-saver';
import { api } from '../../services/api';
import { todayDueDateTimeLocal } from '../../utils/duration';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../common/GlobalNotifications';
import { openAssistant } from '../ai/AssistantLauncher';
import TicketShareCard from './TicketShareCard';

const STATUS_LABEL = { pending: i18n.t('hardcoded.components_admin_ordermanagement.pendiente'), in_progress: i18n.t('hardcoded.pages_checkoutpage.en_progreso'), blocked: i18n.t('hardcoded.components_tickets_ticketactioncenter.bloqueada'), completed: i18n.t('hardcoded.components_tickets_ticketactioncenter.completada'), cancelled: i18n.t('hardcoded.components_admin_ordermanagement.cancelada') };
const waLink = (phone, text) => `https://wa.me/${(phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

export default function TicketActionCenter({ ticket }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canTask = hasPermission('manager');

  const [collab, setCollab] = useState({ tasks: [], channels: [] });
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [taskDlg, setTaskDlg] = useState(null);   // prefill object or null
  const [sugDlg, setSugDlg] = useState(false);
  const [chatDlg, setChatDlg] = useState(null);   // {message?} or null
  const [waDlg, setWaDlg] = useState(null);       // {message?} or null
  const [snapDlg, setSnapDlg] = useState(false);

  const loadCollab = useCallback(async () => {
    try { const r = await api.alarmTickets.ticketCollaboration(ticket.id); setCollab(r.data || { tasks: [], channels: [] }); }
    catch (e) { /* noop */ }
  }, [ticket.id]);
  useEffect(() => { loadCollab(); }, [loadCollab]);

  // Bus de eventos desde la card de IA
  useEffect(() => {
    const onAction = (e) => {
      const { action, message } = e.detail || {};
      if (action === 'task') setTaskDlg(defaultTaskPrefill(ticket));
      else if (action === 'suggested') setSugDlg(true);
      else if (action === 'chat') setChatDlg({ message });
      else if (action === 'whatsapp') setWaDlg({ message });
    };
    const onChanged = () => loadCollab();
    // Acción pedida desde otra pantalla (p. ej. el popup del sidebar navega con
    // ?action=task). Se dispara una sola vez y se limpia la URL para que no se
    // repita al refrescar.
    try {
      const params = new URLSearchParams(window.location.search);
      const requested = params.get('action');
      if (requested) {
        onAction({ detail: { action: requested } });
        params.delete('action');
        const rest = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''));
      }
    } catch (_) { /* la URL nunca puede romper el panel */ }

    window.addEventListener('aps-ticket-action', onAction);
    window.addEventListener('aps-ticket-collab-changed', onChanged);
    return () => { window.removeEventListener('aps-ticket-action', onAction); window.removeEventListener('aps-ticket-collab-changed', onChanged); };
  }, [ticket, loadCollab]);

  const afterChange = () => { loadCollab(); };

  return (
    <Stack spacing={1.5}>
      {/* Barra de acciones */}
      <Paper sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{t('hardcoded.components_tickets_ticketactioncenter.acciones_del_ticket')}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
          <Tooltip title={canTask ? '' : t('hardcoded.components_tickets_ticketactioncenter.requiere_permisos_de_manager')}>
            <span><Button size="small" variant="contained" startIcon={<AddTaskIcon />} disabled={!canTask} onClick={() => setTaskDlg(defaultTaskPrefill(ticket))}>{t('hardcoded.components_tickets_ticketactioncenter.crear_tarea')}</Button></span>
          </Tooltip>
          <Button size="small" variant="outlined" startIcon={<ChatIcon />} onClick={() => setChatDlg({})}>{t('hardcoded.components_tickets_ticketactioncenter.hablar_en_chat')}</Button>
          <Button size="small" variant="outlined" startIcon={<WhatsAppIcon />} onClick={() => setWaDlg({})}>WhatsApp</Button>
          {!isMobile && <>
            {canTask && <Button size="small" variant="outlined" startIcon={<SuggestIcon />} onClick={() => setSugDlg(true)}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.tareas_sugeridas')}</Button>}
            <Button size="small" variant="outlined" startIcon={<SnapIcon />} onClick={() => setSnapDlg(true)}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.generar_captura')}</Button>
            <Button size="small" variant="outlined" startIcon={<AIIcon />} onClick={() => openAssistant(t('hardcoded.components_tickets_ticketactioncenter.analiza_el_ticket_ticket_number_que', { ticket_number: ticket.ticket_number }))}>{t('hardcoded.components_ai_airecommendationspanel.analizar_con_ia')}</Button>
          </>}
          {isMobile && <>
            <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)}><MoreIcon /></IconButton>
            <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
              {canTask && <MenuItem onClick={() => { setMoreAnchor(null); setSugDlg(true); }}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.tareas_sugeridas')}</MenuItem>}
              <MenuItem onClick={() => { setMoreAnchor(null); setSnapDlg(true); }}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.generar_captura')}</MenuItem>
              <MenuItem onClick={() => { setMoreAnchor(null); openAssistant(t('hardcoded.components_tickets_ticketactioncenter.analiza_el_ticket_ticket_number', { ticket_number: ticket.ticket_number })); }}>{t('hardcoded.components_ai_airecommendationspanel.analizar_con_ia')}</MenuItem>
            </Menu>
          </>}
        </Stack>
      </Paper>

      {/* Tareas relacionadas */}
      <Paper sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}><Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.tareas_relacionadas')}</Typography><Chip size="small" label={collab.tasks.length} /></Stack>
        {collab.tasks.length === 0 ? <Typography variant="caption" color="text.secondary">{t('hardcoded.components_tickets_ticketactioncenter.sin_tareas_crea_una_desde')}</Typography> : (
          <List dense disablePadding>
            {collab.tasks.map((t) => (
              <ListItem key={t.id} disableGutters secondaryAction={<IconButton size="small" onClick={() => navigate(`/tasks/${t.id}`)}><OpenIcon fontSize="small" /></IconButton>}>
                <ListItemText primary={t.title} secondary={STATUS_LABEL[t.status] || t.status} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Conversaciones relacionadas */}
      <Paper sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}><Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{t('hardcoded.components_tickets_ticketactioncenter.conversaciones_relacionadas')}</Typography><Chip size="small" label={collab.channels.length} /></Stack>
        {collab.channels.length === 0 ? <Typography variant="caption" color="text.secondary">{t('hardcoded.components_tickets_ticketactioncenter.sin_conversaciones_usa_hablar_en')}</Typography> : (
          <List dense disablePadding>
            {collab.channels.map((c) => (
              <ListItem key={c.channel_id} disableGutters secondaryAction={<Button size="small" onClick={() => navigate(`/chat?channel=${c.channel_id}`)}>{t('hardcoded.components_tickets_ticketactioncenter.abrir')}</Button>}>
                <ListItemText primary={c.name} secondary={c.last_message_at ? new Date(c.last_message_at).toLocaleString('es-MX') : '—'} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {taskDlg && <TaskDialog ticket={ticket} prefill={taskDlg} onClose={() => setTaskDlg(null)} onDone={() => { setTaskDlg(null); afterChange(); }} />}
      {sugDlg && <SuggestedDialog ticket={ticket} onClose={() => setSugDlg(false)} onDone={() => { setSugDlg(false); afterChange(); }} />}
      {chatDlg && <ChatDialog ticket={ticket} initialMessage={chatDlg.message} existing={collab.channels} onClose={() => setChatDlg(null)} onDone={(cid) => { setChatDlg(null); afterChange(); if (cid) navigate(`/chat?channel=${cid}`); }} />}
      {waDlg && <WhatsAppDialog ticket={ticket} overrideMessage={waDlg.message} onClose={() => setWaDlg(null)} />}
      {snapDlg && <SnapshotDialog ticket={ticket} onClose={() => setSnapDlg(false)} />}
    </Stack>
  );
}

function defaultTaskPrefill(ticket) {
  return { title: '', description: '', priority: (['critical', 'extreme'].includes(String(ticket.severity).toLowerCase()) ? 'high' : 'medium'), checklist: [] };
}

// ───────── Crear tarea ─────────
function TaskDialog({ ticket, prefill, onClose, onDone }) {
  const { t } = useTranslation();
  const { showError, showSuccess, showWarning } = useNotification();
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    title: prefill.title || t('hardcoded.components_tickets_ticketactioncenter.revisar_ticket_number_ticketvehicle_n', { ticket_number: ticket.ticket_number, ticketvehicle_n: ticket.vehicle_name || '' }).trim(),
    description: prefill.description || '', priority: prefill.priority || 'medium', assigned_to: '',
    // Una tarea que nace de un ticket se atiende hoy: no se deja sin fecha.
    due_at: todayDueDateTimeLocal(),
    checklist: (prefill.checklist || []).join('\n'),
  }));
  useEffect(() => { api.users.getAll().then((r) => setUsers(r.data || [])).catch(() => {}); }, []);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.title.trim()) { showError(t('hardcoded.components_tickets_ticketactioncenter.titulo_requerido')); return; }
    setSaving(true);
    try {
      const r = await api.alarmTickets.createTicketTask(ticket.id, {
        title: form.title.trim(), description: form.description.trim() || undefined, priority: form.priority,
        assigned_to: form.assigned_to || undefined, due_at: form.due_at || undefined,
        checklist: form.checklist.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      if (r.data?.deduped) {
        // No se creo nada: avisar en amarillo, nunca en verde.
        showWarning(t('hardcoded.components_tickets_ticketactioncenter.ya_existia_una_tarea_abierta'));
      } else {
        showSuccess(t('hardcoded.components_tickets_ticketactioncenter.tarea_creada'));
      }
      onDone(r.data?.task || null, !!r.data?.deduped);
    } catch (e) { showError(e?.response?.data?.detail || t('hardcoded.components_tickets_ticketactioncenter.no_se_pudo_crear')); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('hardcoded.components_tickets_ticketactioncenter.crear_tarea_desde_el_ticket')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <TextField label={t('hardcoded.components_tickets_ticketactioncenter.titulo')} value={form.title} onChange={(e) => set('title', e.target.value)} size="small" autoFocus />
          <TextField label={t('audit.table.description')} value={form.description} onChange={(e) => set('description', e.target.value)} size="small" multiline minRows={2} />
          <Stack direction="row" spacing={1.5}>
            <TextField select label={t('hardcoded.components_tickets_ticketactioncenter.prioridad')} value={form.priority} onChange={(e) => set('priority', e.target.value)} size="small" sx={{ minWidth: 130 }}>
              {['low', 'medium', 'high', 'critical'].map((p) => <MuiMenuItem key={p} value={p}>{p}</MuiMenuItem>)}
            </TextField>
            <TextField type="datetime-local" label={t('hardcoded.components_tickets_ticketactioncenter.vence')} value={form.due_at} onChange={(e) => set('due_at', e.target.value)} size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
          </Stack>
          <Autocomplete options={users} size="small" getOptionLabel={(u) => `${u.name || ''} ${u.last_name || ''}`.trim() || u.email || ''}
            onChange={(_, u) => set('assigned_to', u?.id || '')} renderInput={(p) => <TextField {...p} label={t('hardcoded.components_tickets_ticketactioncenter.responsable_opcional')} />} />
          <TextField label={t('hardcoded.components_tickets_ticketactioncenter.checklist_uno_por_linea')} value={form.checklist} onChange={(e) => set('checklist', e.target.value)} size="small" multiline minRows={3} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t('common.cancel')}</Button><Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>{t('audit.actions.CREATE')}</Button></DialogActions>
    </Dialog>
  );
}

// ───────── Tareas sugeridas ─────────
function SuggestedDialog({ ticket, onClose, onDone }) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const [sug, setSug] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.alarmTickets.ticketTaskSuggestions(ticket.id).then((r) => { const s = r.data.suggestions || []; setSug(s); setSel(Object.fromEntries(s.map((_, i) => [i, true]))); }).catch(() => {}).finally(() => setLoading(false));
  }, [ticket.id]);
  const create = async () => {
    setSaving(true);
    try {
      let n = 0;
      for (let i = 0; i < sug.length; i++) {
        if (!sel[i]) continue;
        await api.alarmTickets.createTicketTask(ticket.id, { title: sug[i].title, description: sug[i].description, priority: sug[i].priority, checklist: sug[i].checklist });
        n++;
      }
      showSuccess(t('hardcoded.components_tickets_ticketactioncenter.n_tareas_creadas', { n: n })); onDone();
    } catch (e) { showError(t('hardcoded.components_tickets_ticketactioncenter.no_se_pudieron_crear_todas')); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('hardcoded.components_tickets_ticketactioncenter.tareas_sugeridas_para_este_ticket')}</DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></Box> : (
          <Stack spacing={1}>
            {sug.map((s, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1 }}>
                <FormControlLabel control={<Checkbox checked={!!sel[i]} onChange={(e) => setSel((p) => ({ ...p, [i]: e.target.checked }))} />}
                  label={<TextField variant="standard" fullWidth value={s.title} onChange={(e) => setSug((p) => p.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />} sx={{ alignItems: 'center', width: '100%', m: 0 }} />
                <Box component="ul" sx={{ m: 0, pl: 4 }}>{(s.checklist || []).map((c, k) => <Typography key={k} component="li" variant="caption" color="text.secondary">{c}</Typography>)}</Box>
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t('common.cancel')}</Button><Button variant="contained" onClick={create} disabled={saving || !Object.values(sel).some(Boolean)}>{t('hardcoded.components_tickets_ticketactioncenter.crear_seleccionadas')}</Button></DialogActions>
    </Dialog>
  );
}

// ───────── Chat ─────────
function ChatDialog({ ticket, initialMessage, existing, onClose, onDone }) {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [message, setMessage] = useState(initialMessage || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.users.getAll().then((r) => setUsers(r.data || [])).catch(() => {}); }, []);
  const create = async () => {
    setSaving(true);
    try {
      const r = await api.alarmTickets.createTicketChannel(ticket.id, { member_user_ids: members.map((m) => m.id), message: message || undefined });
      onDone(r.data.channel_id);
    } catch (e) { showError(e?.response?.data?.detail || t('hardcoded.components_tickets_ticketactioncenter.no_se_pudo_crear_el')); setSaving(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('hardcoded.components_tickets_ticketactioncenter.hablar_sobre_este_ticket')}</DialogTitle>
      <DialogContent dividers>
        {existing?.length > 0 && (
          <Alert severity="info" sx={{ mb: 1.5 }}>{t('hardcoded.components_tickets_ticketactioncenter.ya_existe')}{existing.length} {t('hardcoded.components_tickets_ticketactioncenter.conversaciones_para_este_ticket')}<Button size="small" onClick={() => onDone(existing[0].channel_id)} sx={{ ml: 1 }}>{t('hardcoded.components_tickets_ticketactioncenter.abrir_canal')}</Button>
          </Alert>
        )}
        <Stack spacing={1.5}>
          <Autocomplete multiple options={users} value={members} onChange={(_, v) => setMembers(v)} size="small"
            getOptionLabel={(u) => `${u.name || ''} ${u.last_name || ''}`.trim() || u.email || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(p) => <TextField {...p} label={t('hardcoded.components_tickets_ticketactioncenter.invitar_usuarios_de_tu_empresa')} />} />
          <TextField label={t('hardcoded.components_tickets_ticketactioncenter.mensaje_inicial_opcional_si_vacio')} value={message} onChange={(e) => setMessage(e.target.value)} size="small" multiline minRows={3} />
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t('common.cancel')}</Button><Button variant="contained" onClick={create} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <ChatIcon />}>{t('hardcoded.components_tickets_ticketactioncenter.crear_canal_e_ir')}</Button></DialogActions>
    </Dialog>
  );
}

// ───────── WhatsApp ─────────
function WhatsAppDialog({ ticket, overrideMessage, onClose }) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');
  const [manual, setManual] = useState('');
  const [detail, setDetail] = useState(true);
  const [loading, setLoading] = useState(true);
  const fetchMsg = useCallback((inc) => {
    setLoading(true);
    api.alarmTickets.whatsappMessage(ticket.id, { include_detail: inc }).then((r) => { setData(r.data); setMessage(overrideMessage || r.data.message); }).catch(() => showError(t('hardcoded.components_tickets_ticketactioncenter.no_se_pudo_preparar_el'))).finally(() => setLoading(false));
  }, [ticket.id, overrideMessage, showError]);
  useEffect(() => { fetchMsg(detail); }, [fetchMsg, detail]);
  const copy = () => { navigator.clipboard?.writeText(message); showSuccess(t('hardcoded.components_tickets_ticketactioncenter.mensaje_copiado')); };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('hardcoded.components_tickets_ticketactioncenter.compartir_por_whatsapp')}</DialogTitle>
      <DialogContent dividers>
        <FormControlLabel control={<Checkbox checked={detail} onChange={(e) => setDetail(e.target.checked)} />} label={t('hardcoded.components_tickets_ticketactioncenter.incluir_detalle_del_ticket')} />
        <TextField fullWidth multiline minRows={5} size="small" value={message} onChange={(e) => setMessage(e.target.value)} sx={{ mb: 1 }} />
        <Button size="small" startIcon={<CopyIcon />} onClick={copy} sx={{ mb: 1 }}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.copiar_mensaje')}</Button>
        <Divider textAlign="left"><Typography variant="caption" color="text.secondary">{t('hardcoded.components_tickets_ticketactioncenter.enviar_a')}</Typography></Divider>
        {loading ? <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box> : (
          <List dense>
            {(data?.recipients || []).map((r, i) => (
              <ListItem key={i} disableGutters secondaryAction={<Button size="small" variant="outlined" startIcon={<WhatsAppIcon />} onClick={() => window.open(waLink(r.phone, message), '_blank', 'noopener')}>{t('hardcoded.components_chat_videonoterecorder.enviar')}</Button>}>
                <ListItemText primary={r.name} secondary={`${r.type} · ${r.phone}`} />
              </ListItem>
            ))}
            {(!data?.recipients || data.recipients.length === 0) && <Typography variant="caption" color="text.secondary">{t('hardcoded.components_tickets_ticketactioncenter.ningun_destinatario_con_telefono_usa')}</Typography>}
          </List>
        )}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <TextField size="small" label={t('hardcoded.components_tickets_ticketactioncenter.numero_manual_con_codigo_de')} value={manual} onChange={(e) => setManual(e.target.value)} sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" disabled={!manual.replace(/\D/g, '')} startIcon={<WhatsAppIcon />} onClick={() => window.open(waLink(manual, message), '_blank', 'noopener')}>{t('hardcoded.components_chat_videonoterecorder.enviar')}</Button>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t('common.close')}</Button></DialogActions>
    </Dialog>
  );
}

// ───────── Captura ─────────
function SnapshotDialog({ ticket, onClose }) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const cardRef = useRef(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.alarmTickets.ticketSummary(ticket.id).then((r) => setSummary(r.data)).catch(() => showError(t('hardcoded.components_tickets_ticketactioncenter.no_se_pudo_cargar_el'))); }, [ticket.id, showError]);
  const download = async () => {
    if (!cardRef.current) return; setBusy(true);
    try { const png = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: '#ffffff' }); saveAs(png, `${ticket.ticket_number}.png`); }
    catch (e) { showError(t('hardcoded.components_tickets_ticketactioncenter.no_se_pudo_generar_la')); } finally { setBusy(false); }
  };
  const copy = async () => {
    if (!cardRef.current || !navigator.clipboard?.write) { showError(t('hardcoded.components_tickets_ticketactioncenter.tu_navegador_no_soporta_copiar')); return; }
    setBusy(true);
    try { const blob = await toBlob(cardRef.current, { pixelRatio: 2, backgroundColor: '#ffffff' }); await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]); showSuccess(t('hardcoded.components_tickets_ticketactioncenter.imagen_copiada')); }
    catch (e) { showError(t('hardcoded.components_tickets_ticketactioncenter.no_se_pudo_copiar_la')); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('hardcoded.components_tickets_ticketactioncenter.captura_del_ticket')}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f7' }}>
        {summary ? <Box sx={{ boxShadow: 3 }}><TicketShareCard ref={cardRef} summary={summary} /></Box> : <CircularProgress />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button startIcon={<CopyIcon />} onClick={copy} disabled={busy || !summary}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.copiar')}</Button>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={download} disabled={busy || !summary}>{i18n.t('hardcoded.components_tickets_ticketactioncenter.descargar_png')}</Button>
      </DialogActions>
    </Dialog>
  );
}
